// Classes.
import Handler from "./Handler";

// Types.
import type event from "../Types/Event";

declare global {
    interface Window {
        _globalScrollyListener: Listener;
    }
}

/**
 * Scrolly.Listener
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-10 Free of TS errors, renamed callbacks to handlers.
 * @package fk_js/scrolly
 * @version: 1.1.0
 */
class Listener
{

    private handlers :Set<Handler> = new Set();
    private queuedAnimationFrames :Function[] = [];

    public resizeTimeout :ReturnType<typeof setTimeout>|undefined;

    /**
     * Constructor.
     */
    constructor()
    {
    }

    // ###############
    // Private methods
    // ###############

    private queueAnimationFrame(animationFrame :Function) :void
    {
        this.queuedAnimationFrames.push(animationFrame);
    }

    private queueAnimationFrames(animationFrames :Function[]) :void
    {
        animationFrames.forEach((animationFrame :Function) :void => {
            this.queueAnimationFrame(animationFrame);
        })
    }

    private requestAnimationFrames() :void
    {
        this.queuedAnimationFrames.forEach((animationFrame :Function) :void => {
            requestAnimationFrame(() => animationFrame());
        });
        this.queuedAnimationFrames = [];
    }

    // ##############
    // Public methods
    // ##############

    /**
     * Add a scroll handler to the global listener.
     */
    public addHandler(handler :Handler) :Listener
    {
        // TODO: Some callbacks should be unique per site (like ScrollDirectionObserver) => there must not be added twice or more
        this.handlers.add(handler);
        return this;
    }

    /**
     * Handle all added/registered events on scroll/resize.
     */
    public handle(e :Event) :void
    {
        for (const handler of this.handlers) {
            if (
                !handler.listensTo(e.type as event) ||
                !handler.isEnabled() ||
                !e.target ||
                !handler.appliesTo(e.target)
            ) {
                continue;
            }
            this.queueAnimationFrames(handler.requestAnimationFrames(e, this));
        }
        this.requestAnimationFrames();
    }

    // ##############
    // Static methods
    // ##############

    /**
     * Initialize the global scroll handler.
     */
    static init() :Listener
    {
        if (!window._globalScrollyListener) {
            window._globalScrollyListener = new Listener();
            window.addEventListener('init', (event :Event) => window._globalScrollyListener.handle(event), { passive: true });
            window.addEventListener('scroll', (event :Event) => window._globalScrollyListener.handle(event), { passive: true });
            window.addEventListener('wheel', (event :Event) => window._globalScrollyListener.handle(event), { passive: false });
            window.addEventListener('touchmove', (event :Event) => window._globalScrollyListener.handle(event), { passive: false });
            window.addEventListener('resize', (event :Event) => {
                clearTimeout(window._globalScrollyListener.resizeTimeout);
                window._globalScrollyListener.resizeTimeout = setTimeout(() => {
                    window._globalScrollyListener.handle(event);
                }, 500);
            });
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('init'));
            }, 1);
        }
        return window._globalScrollyListener;
    }

    /**
     * Subscribe a scroll callback to the global scroll handler.
     */
    static subscribe(callback :Handler) :Listener
    {
        Listener.init();
        window._globalScrollyListener.addHandler(callback);
        return window._globalScrollyListener;
    }

}

export default Listener;